import React, { useState, useEffect, useLayoutEffect, useRef, useContext, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import _ from '@/utils/lodash';
import Header from '@components/layouts/Header';
import Menu from '@components/layouts/Menu';
import ModuleAttachments from '@components/shared/ModuleAttachments';
import SubsiteBar from '@components/shared/SubsiteBar';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';
import { useAppContext } from '@/context/AppContext';
import { findPageByPath, findPageBySlug, findPageByPathWithId, resolveInheritedThemeColor } from '@/utils/pagePaths';
import {
    trackPageView,
    ensureAnalyticsOptOutPersisted,
    disableUmamiForPreview,
} from '@/services/analytics';
import { APPEARANCE_PREVIEW_QUERY } from '@/theme/tokens';
import { RealPathnameContext } from '@/router/realPathname';
import { compactMeta, shareImageMeta, shareSiteTitle } from '@/share/meta';
import { resolvePageShareTitle, resolveShareTitle, shareTitleVarsForPage } from '@/share/titleTemplate';
import { installAdminSaveShortcut, installFrameSaveShortcut } from '@/admin/saveShortcut';
import { ENABLE_VIEWER_PAGE_FADES, FADE_DURATION as VIEWER_FADE_DURATION } from '@components/shared/ImageViewer/imageViewerUtils';
const ThemePreviewBridge = lazy(() => import('@components/preview/ThemePreviewBridge'));
const IMAGE_FADE_DURATION = VIEWER_FADE_DURATION; // Match ImageViewer's FADE_DURATION
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Root Layout - wraps all public pages
 * Uses semantic HTML: main, article structure
 */
function RootLayout({ match: matchProp, children, location: locationProp }) {
    const hookLocation = useLocation();
    const hookParams = useParams();
    const location = locationProp || hookLocation;
    const match = matchProp || { params: hookParams };
    const routeChildren = children ?? <Outlet />;
    const { content, urlBase, url, showMenu, toggleMenu, theme } = useAppContext();
    const pathname = location?.pathname || '';
    const slug = match?.params?.slug || hookParams?.['*'] || '';

    // Media viewer routes hide site chrome (image .img and video .vid).
    const isImageRoute = pathname.endsWith('.img')
        || pathname.endsWith('.vid')
        || match?.params?.image
        || match?.params?.video;
    
    // Route display for image open/close overlays. Page-to-page nav swaps instantly.
    const [transitionStage, setTransitionStage] = useState('idle'); // 'idle' | 'exiting' | 'entering'
    const [displayChildren, setDisplayChildren] = useState(routeChildren);
    const nextChildrenRef = React.useRef(routeChildren);
    const prevPathRef = React.useRef(pathname);
    const prevIsImageRouteRef = React.useRef(isImageRoute);
    
    // Image transition overlay states
    const [imageUnderlayContent, setImageUnderlayContent] = useState(null); // Old page when opening image
    // After closing the viewer the page below stays hidden until it is scrolled to
    // the picture. Hiding it, not covering it: an <Outlet /> kept for a cover
    // renders the new route too, so the page would show twice.
    const [revealPending, setRevealPending] = useState(false);
    const [disableTransitions, setDisableTransitions] = useState(false); // Disable all CSS transitions during pre-render
    const pageArticleRef = useRef(null);
    const imageTransitionTimeoutRef = useRef(null);
    const closeTokenRef = useRef(0);
    // Snapshot of the last idle page — used as underlay/overlay when the path changes
    // (live <Outlet /> is a new element every render, so we cannot freeze it via setState).
    const idleContentRef = useRef(routeChildren);

    useEffect(() => {
        // Keep the latest route element for transition swaps. Do not put it in
        // effect deps — <Outlet /> is a new object every render and would loop.
        nextChildrenRef.current = routeChildren;

        // Same path: leave displayChildren alone; render uses live Outlet when idle.
        if (prevPathRef.current === pathname) {
            idleContentRef.current = routeChildren;
            prevIsImageRouteRef.current = isImageRoute;
            return undefined;
        }

        const previousContent = idleContentRef.current;
        const closeToken = ++closeTokenRef.current;
        // A close still in progress is abandoned; its page must not stay hidden.
        setRevealPending(false);
        setDisableTransitions(false);

        // Skip transition when navigating between images
        const wasImageRoute = prevIsImageRouteRef.current;
        const isNavigatingBetweenImages = wasImageRoute && isImageRoute;

        if (isNavigatingBetweenImages) {
            // Instant swap for image navigation (no transition)
            setDisplayChildren(routeChildren);
            idleContentRef.current = routeChildren;
            prevPathRef.current = pathname;
            prevIsImageRouteRef.current = isImageRoute;
            setTransitionStage('idle');
            return undefined;
        }
        
        // Opening image: keep old page content as underlay while ImageViewer fades in
        if (!wasImageRoute && isImageRoute) {
            // Clear any pending transitions
            clearTimeout(imageTransitionTimeoutRef.current);
            
            // Keep old page content visible as underlay (only useful while the viewer fades)
            setImageUnderlayContent(ENABLE_VIEWER_PAGE_FADES ? previousContent : null);
            // Set new content (ImageViewer) - it handles its own fade-in
            setDisplayChildren(routeChildren);
            idleContentRef.current = routeChildren;
            setTransitionStage('idle');
            
            // After ImageViewer fade-in completes, remove underlay
            if (ENABLE_VIEWER_PAGE_FADES) {
                imageTransitionTimeoutRef.current = setTimeout(() => {
                    setImageUnderlayContent(null);
                }, IMAGE_FADE_DURATION);
            }
            
            prevPathRef.current = pathname;
            prevIsImageRouteRef.current = isImageRoute;
            return undefined;
        }
        
        // Closing image: pre-render new page, scroll to image, then fade out ImageViewer
        if (wasImageRoute && !isImageRoute) {
            // Clear any pending transitions
            clearTimeout(imageTransitionTimeoutRef.current);
            
            // Extract media ID and page path from the previous path (e.g., /page/imageId.img -> imageId, page)
            const prevPath = prevPathRef.current;
            const imageIdMatch = prevPath.match(/\/([^/]+)\.(?:img|vid)$/);
            const closingImageId = imageIdMatch ? imageIdMatch[1] : null;
            
            // Extract the page path from the previous media route
            const prevPagePath = prevPath.replace(/^\//, '').replace(/\/[^/]+\.(?:img|vid)$/, '');
            const prevPageResult = prevPagePath ? findPageByPathWithId(prevPagePath, pages) : null;
            const prevPage = prevPageResult?.page || null;
            const prevPageId = prevPageResult?.id || null;
            const isFromEditorial = prevPage?.gallery?.type === 'slideshow';
            
            // Check if we're navigating to a different page (e.g., parent page from editorial)
            const currentPagePath = pathname.replace(/^\//, '');
            const isNavigatingToParent = isFromEditorial && currentPagePath !== prevPagePath;
            
            // Disable all CSS transitions for instant pre-render
            setDisableTransitions(true);
            setRevealPending(true);
            setDisplayChildren(routeChildren);
            idleContentRef.current = routeChildren;
            setTransitionStage('idle');

            // Center a target element in the viewport immediately.
            // Doing this before overlay fade-out avoids the visible "jump to top, then scroll back" lag.
            const centerElementInViewport = (targetEl) => {
                if (!targetEl) return;
                const viewportHeight = window.innerHeight;
                const rect = targetEl.getBoundingClientRect();
                const scrollTop = window.scrollY || document.documentElement.scrollTop;
                const elementTop = rect.top + scrollTop;
                const elementCenter = elementTop + rect.height / 2;
                const targetScroll = elementCenter - viewportHeight / 2;
                window.scrollTo(0, Math.max(0, targetScroll));
            };
            
            // The page shows only once it is positioned. The fallback timer covers
            // stalled animation frames (background tabs).
            let revealed = false;
            const revealPage = () => {
                if (revealed || closeTokenRef.current !== closeToken) return;
                revealed = true;
                clearTimeout(imageTransitionTimeoutRef.current);
                setRevealPending(false);
                setDisableTransitions(false);
            };

            // After the new page renders, scroll to center the image or directory tile, then start fade-out
            // Poll for the target element to have valid dimensions (masonry layout complete)
            let pollAttempts = 0;
            const MAX_POLL_ATTEMPTS = 100; // Prevent infinite loop
            const scrollToTarget = () => {
                if (closeTokenRef.current !== closeToken) return;
                pollAttempts++;
                const selector = isNavigatingToParent && prevPageId
                    ? `[data-page-id="${prevPageId}"]`
                    : closingImageId
                        ? `[data-image-id="${closingImageId}"]`
                        : null;
                
                if (!selector) {
                    revealPage();
                    return;
                }
                
                const targetElement = document.querySelector(selector);

                // The page is drawn but has no such tile (a text page linking to the
                // closed page): nothing to scroll to.
                if (!targetElement && pageArticleRef.current?.childElementCount) {
                    scrollToTarget.missingFrames = (scrollToTarget.missingFrames || 0) + 1;
                    if (scrollToTarget.missingFrames >= 3) {
                        delete scrollToTarget.missingFrames;
                        revealPage();
                        return;
                    }
                }
                
                // Check if element exists and has valid dimensions (layout complete)
                if (targetElement) {
                    const rect = targetElement.getBoundingClientRect();
                    
                    if (rect.height > 0) {
                        // Wait for position to stabilize - check if position changed from previous frame
                        if (pollAttempts === 1 || !scrollToTarget.lastRect) {
                            // First time seeing valid dimensions - store position and wait one more frame
                            scrollToTarget.lastRect = rect;
                            scrollToTarget.stableFrames = 1;
                            requestAnimationFrame(scrollToTarget);
                            return;
                        }
                        
                        // Check if position is stable (within 5px tolerance)
                        const positionChanged = Math.abs(rect.top - scrollToTarget.lastRect.top) > 5 || 
                                               Math.abs(rect.left - scrollToTarget.lastRect.left) > 5;
                        
                        if (positionChanged) {
                            // Position still changing - wait for it to stabilize
                            scrollToTarget.lastRect = rect;
                            scrollToTarget.stableFrames = 1;
                            requestAnimationFrame(scrollToTarget);
                            return;
                        }
                        
                        // Position is stable - increment stable frame count
                        scrollToTarget.stableFrames = (scrollToTarget.stableFrames || 0) + 1;
                        
                        // Require 2 consecutive stable frames before scrolling (ensures layout is truly settled)
                        if (scrollToTarget.stableFrames < 2) {
                            scrollToTarget.lastRect = rect;
                            requestAnimationFrame(scrollToTarget);
                            return;
                        }
                        
                        // Position is stable - scroll now (before fade-out starts)
                        // Clear stability tracking
                        delete scrollToTarget.lastRect;
                        delete scrollToTarget.stableFrames;

                        centerElementInViewport(targetElement);
                        revealPage();
                        return;
                    }
                }
                
                // Element not ready yet - wait for next frame and try again
                if (pollAttempts < MAX_POLL_ATTEMPTS) {
                    requestAnimationFrame(scrollToTarget);
                } else {
                    revealPage();
                }
            };
            
            // Start polling after initial render
            requestAnimationFrame(scrollToTarget);
            imageTransitionTimeoutRef.current = setTimeout(() => {
                const target = closingImageId && document.querySelector(`[data-image-id="${closingImageId}"]`);
                if (target) centerElementInViewport(target);
                revealPage();
            }, 1200);
            
            prevPathRef.current = pathname;
            prevIsImageRouteRef.current = isImageRoute;
            return undefined;
        }

        // Path changed -> show the new page immediately (no fade/slide).
        setDisplayChildren(routeChildren);
        idleContentRef.current = routeChildren;
        prevPathRef.current = pathname;
        prevIsImageRouteRef.current = isImageRoute;
        setTransitionStage('idle');
        return undefined;
    }, [pathname, isImageRoute]);

    // Always keep the latest outlet element available to transition handlers.
    nextChildrenRef.current = routeChildren;
    if (transitionStage === 'idle' && !imageUnderlayContent) {
        idleContentRef.current = routeChildren;
    }
    const pageContent = (
        transitionStage === 'idle'
        && !imageUnderlayContent
    ) ? routeChildren : displayChildren;

    // Appearance iframe: latch true once we see ?appearancePreview=1 so soft-nav
    // (which drops the query from the SPA URL) does not unmount ThemePreviewBridge
    // and stop address-bar updates. Initial value comes from the router location so
    // SSR and the first client paint match.
    const [isAppearancePreview] = useState(() => (
        new URLSearchParams(location?.search || '').get(APPEARANCE_PREVIEW_QUERY) === '1'
    ));

    // Track pageviews for analytics (only when pathname changes)
    // Uses a separate ref to avoid interfering with transition logic
    // Note: PublicPageRoute tracks project views with full context,
    // this tracks the basic pageview with page name when available
    const realPathname = useContext(RealPathnameContext) || pathname;
    const analyticsPathRef = useRef(realPathname);
    const pagesForAnalytics = content?.pages || {};

    // If this browser has ever logged into the admin (persistent umami.disabled
    // flag) but its cookie was cleared, restore the skip cookie so the server
    // stops embedding the Umami script again.
    useEffect(() => {
        ensureAnalyticsOptOutPersisted();
    }, []);

    // Editing surfaces never want the browser's "Save page" dialog. In the
    // Appearance iframe the shortcut is handed to the admin's save toast.
    useEffect(() => {
        if (!isAppearancePreview && !window.__ADMIN__) return undefined;
        return window.parent !== window ? installFrameSaveShortcut() : installAdminSaveShortcut();
    }, [isAppearancePreview]);

    useEffect(() => {
        if (isAppearancePreview) {
            disableUmamiForPreview();
            return;
        }
        // Only track when pathname actually changes (not on scroll or state updates)
        if (realPathname !== analyticsPathRef.current) {
            analyticsPathRef.current = realPathname;
            
            // Get page info for better tracking
            const urlPath = realPathname.replace(/^\//, '').replace(/\.(?:img|vid)$/, '');
            let pageName = null;
            let pageType = null;
            
            if (realPathname === '/' || realPathname === '') {
                pageName = 'Home';
                pageType = 'home';
            } else if (urlPath) {
                let page = findPageByPath(urlPath, pagesForAnalytics);
                if (!page && !urlPath.includes('/')) {
                    const result = findPageBySlug(urlPath, pagesForAnalytics);
                    page = result?.page || null;
                }
                if (page) {
                    pageName = page.name;
                    pageType = page.gallery?.type || 'html';
                }
            }
            
            trackPageView({ pageName, pageType });
        }
    }, [realPathname, pagesForAnalytics, isAppearancePreview]);
    
    // Detect 404 by checking if the current path matches any known page
    // This works synchronously during render (unlike children inspection which can't see inside React Router's Switch)
    const pages = content?.pages || {};
    const isNotFound = React.useMemo(() => {
        // Home page is always valid
        if (pathname === '/' || pathname === '') return false;
        
        // Image routes are handled separately
        if (isImageRoute) return false;
        
        // Extract the page path from pathname (remove leading slash)
        const urlPath = pathname.replace(/^\//, '').replace(/\.(?:img|vid)$/, '');
        if (!urlPath) return false;
        
        // Try to find page by full path first (for nested pages)
        let page = findPageByPath(urlPath, pages);
        // Fallback to slug lookup for single-segment paths
        if (!page && !urlPath.includes('/')) {
            const result = findPageBySlug(urlPath, pages);
            page = result?.page || null;
        }
        
        return page === null;
    }, [pathname, pages, isImageRoute]);

    const properties = content?.properties || {};
    // pages already defined above for 404 detection
    const pagesList = content?.pages ? _.chain(content.pages).map().sortBy('ord').value() : [];
    // For og:image: prefer home.og.image if set, otherwise fall back to legacy ogImage, then first gallery image
    const homeOgImageId = content?.home?.og?.image || content?.home?.ogImage || _.result(content, 'home.gallery.images[0]', null);
    // Use 768px webp from srcset, fallback to original if srcset not available
    const homeImageFile = homeOgImageId ? _.result(content, ['files', homeOgImageId], null) : null;
    // For og:description: prefer home.og.description if set, otherwise use site meta_description
    const homeOgDescriptionRaw = content?.home?.og?.description || properties.meta_description || '';
    const siteTitle = shareSiteTitle(content, theme);
    const homeOgDescription = homeOgDescriptionRaw
        ? resolveShareTitle(
            homeOgDescriptionRaw,
            shareTitleVarsForPage(content?.home, { pages, siteTitle }),
            '',
            { strict: true },
        )
        : '';
    const homeOgTitle = content?.home?.og?.title
        ? resolvePageShareTitle(content.home, { pages, siteTitle })
        : (properties.meta_title || siteTitle);

    // Get current page for per-page theme colors
    const currentPage = React.useMemo(() => {
        if (pathname === '/' || pathname === '') {
            return content?.home || null;
        }
        
        // For image routes, extract the page path (remove the image segment)
        let urlPath = pathname.replace(/^\//, '');
        if (isImageRoute) {
            // Remove the media suffix and the media ID segment
            // e.g., /page/imageId.img -> page
            const segments = urlPath.replace(/\.(?:img|vid)$/, '').split('/');
            segments.pop(); // Remove image ID
            urlPath = segments.join('/');
            if (!urlPath) return content?.home || null; // Home image route
        }
        
        if (!urlPath) return null;
        
        // Try full path first
        let page = findPageByPath(urlPath, pages);
        // Fallback to slug lookup
        if (!page && !urlPath.includes('/')) {
            const result = findPageBySlug(urlPath, pages);
            page = result?.page || null;
        }
        return page;
    }, [pathname, pages, isImageRoute, content?.home]);

    // Fullscreen layout hides the header and menu even when its images are off.
    // The slideshow itself still waits for an enabled set.
    const isBackgroundMode = React.useMemo(() => {
        if (!currentPage) return false;
        return (currentPage.gallery?.type || 'none') === 'background';
    }, [currentPage]);

    // Per-page theme colours (and ancestors'). Only an explicit override gets an
    // inline style; everything else inherits the site theme from the :root block
    // that the server rendered. Writing the site colour inline as well would shadow
    // :root with a copy of itself, which also blocked the Appearance preview from
    // showing a different site colour.
    const pageBackground = resolveInheritedThemeColor(currentPage, pages, 'themeBackground');
    const pageText = resolveInheritedThemeColor(currentPage, pages, 'themeText');
    const pageSideMenuBackground = resolveInheritedThemeColor(currentPage, pages, 'themeSideMenuBackground');
    const pageSideMenuText = resolveInheritedThemeColor(currentPage, pages, 'themeSideMenuText');
    const pageHeaderBackground = resolveInheritedThemeColor(currentPage, pages, 'themeHeaderBackground');
    const pageHeaderText = resolveInheritedThemeColor(currentPage, pages, 'themeHeaderText');
    const pageCaption = resolveInheritedThemeColor(currentPage, pages, 'themeCaption');
    const themeBackground = pageBackground || properties.color_background || '#ffffff';
    const themeText = pageText || properties.color_text || '#1e293b';

    // Apply per-page colours to the document root so html/body, overscroll, and
    // portalled UI match the page. Layout effect: before paint, so the site
    // theme in :root never shows as a second shade under a short page shell.
    useIsoLayoutEffect(() => {
        const root = document.documentElement;
        if (pageBackground) root.style.setProperty('--color-background', pageBackground);
        else root.style.removeProperty('--color-background');

        if (pageText) root.style.setProperty('--color-text', pageText);
        else root.style.removeProperty('--color-text');

        if (pageSideMenuBackground) {
            root.style.setProperty('--color-side-menu-background', pageSideMenuBackground);
        } else {
            root.style.removeProperty('--color-side-menu-background');
        }

        if (pageSideMenuText) root.style.setProperty('--color-side-menu-text', pageSideMenuText);
        else root.style.removeProperty('--color-side-menu-text');

        if (pageHeaderBackground) root.style.setProperty('--color-header-background', pageHeaderBackground);
        else root.style.removeProperty('--color-header-background');

        if (pageHeaderText) root.style.setProperty('--color-header-text', pageHeaderText);
        else root.style.removeProperty('--color-header-text');

        if (pageCaption) root.style.setProperty('--caption-color', pageCaption);
        else root.style.removeProperty('--caption-color');
    }, [pageBackground, pageText, pageSideMenuBackground, pageSideMenuText, pageHeaderBackground, pageHeaderText, pageCaption]);

    // Fullscreen background pages fill the whole screen and never scroll.
    // iOS Safari only paints page pixels behind its status bar and toolbar while
    // the document is scrolled, so touch devices get a runway the page starts
    // scrolled into (see .fs-stage in globals.css); gestures are blocked instead
    // of locking the body, which would make Safari fall back to a solid bar.
    useIsoLayoutEffect(() => {
        if (!isBackgroundMode) return undefined;
        const html = document.documentElement;
        const runway = window.matchMedia('(pointer: coarse)').matches;
        const settle = () => {
            if (!runway) return;
            const offset = parseFloat(getComputedStyle(html).getPropertyValue('--fs-top-bleed')) || 0;
            if (Math.abs(window.scrollY - offset) > 0.5) {
                window.scrollTo({ top: offset, left: 0, behavior: 'instant' });
            }
        };
        settle();
        const frame = requestAnimationFrame(settle);
        const block = (event) => {
            if (event.cancelable) event.preventDefault();
        };
        const options = { passive: false };
        document.addEventListener('touchmove', block, options);
        document.addEventListener('wheel', block, options);
        window.addEventListener('resize', settle);
        window.addEventListener('scroll', settle, { passive: true });
        return () => {
            cancelAnimationFrame(frame);
            document.removeEventListener('touchmove', block, options);
            document.removeEventListener('wheel', block, options);
            window.removeEventListener('resize', settle);
            window.removeEventListener('scroll', settle);
            if (runway) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        };
    }, [isBackgroundMode]);

    const baseUrl = urlBase.replace(/\/$/, '');
    const meta = compactMeta([
        { httpEquiv: 'x-ua-compatible', content: 'ie=edge' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0, viewport-fit=cover' },
        { name: 'description', content: homeOgDescription },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: `${baseUrl}${(url.startsWith('/') ? url : '/' + url).split('?')[0]}` },
        { property: 'og:site_name', content: siteTitle },
        { property: 'og:description', content: homeOgDescription },
        { property: 'og:title', content: homeOgTitle },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: homeOgTitle },
        { name: 'twitter:description', content: homeOgDescription },
        ...shareImageMeta(homeImageFile, baseUrl),
    ]);
    
    return (
        <div 
            id="app-container" 
            className={classNames(
                // Flex-based root that fills the viewport
                'w-full bg-background flex flex-col',
                { 'show-menu': showMenu },
                { 'no-transitions': disableTransitions }
            )}
            style={{
                minHeight: '100dvh',
                // Only a page with its own colours overrides here; otherwise the site
                // theme from :root applies (see the effect above).
                ...(pageBackground ? { '--color-background': pageBackground } : {}),
                ...(pageText ? { '--color-text': pageText } : {}),
                ...(pageSideMenuBackground
                    ? { '--color-side-menu-background': pageSideMenuBackground }
                    : {}),
                ...(pageSideMenuText ? { '--color-side-menu-text': pageSideMenuText } : {}),
                ...(pageHeaderBackground ? { '--color-header-background': pageHeaderBackground } : {}),
                ...(pageHeaderText ? { '--color-header-text': pageHeaderText } : {}),
            }}
        >
            <style dangerouslySetInnerHTML={{ __html: `
                /* Disable all transitions during pre-render */
                .no-transitions,
                .no-transitions *,
                .no-transitions *::before,
                .no-transitions *::after {
                    transition: none !important;
                    animation: none !important;
                }
                #app-container {
                    color: var(--color-text);
                }
                /* Theme colours change with the page. Links must not fade across that cut. */
                #app-container #wrapper a {
                    transition: color 0ms;
                    color: inherit;
                }
                /* Same specificity as the document-head theme, later in the tree, so
                   html/body use the page colour on first paint — not the site colour
                   behind a shorter #app-container. */
                ${pageBackground || pageText || pageSideMenuBackground || pageSideMenuText || pageHeaderBackground || pageHeaderText ? `:root {
                    ${pageBackground ? `--color-background: ${pageBackground};` : ''}
                    ${pageText ? `--color-text: ${pageText};` : ''}
                    ${pageSideMenuBackground ? `--color-side-menu-background: ${pageSideMenuBackground};` : ''}
                    ${pageSideMenuText ? `--color-side-menu-text: ${pageSideMenuText};` : ''}
                    ${pageHeaderBackground ? `--color-header-background: ${pageHeaderBackground};` : ''}
                    ${pageHeaderText ? `--color-header-text: ${pageHeaderText};` : ''}
                }` : ''}
            `}} />
            <Helmet
                titleTemplate={siteTitle ? `%s | ${siteTitle}` : '%s'}
                defaultTitle={siteTitle}
                base={{ href: '/' }}
                meta={meta}
                // A published theme emits its own versioned apple-touch-icon in the
                // document head, so this static fallback would duplicate it.
                link={theme?.brand?.favicon?.derived
                    ? []
                    : [{ rel: 'apple-touch-icon', href: 'apple-touch-icon.png' }]}
            />
            {/* Only inside the Appearance editor's iframe; never for real visitors. */}
            {isAppearancePreview && (
                <Suspense fallback={null}>
                    <ThemePreviewBridge />
                </Suspense>
            )}
            <ModuleAttachments />
            {content?.subsite && (content.subsite.public === false || content.subsite.protected) && (
                <SubsiteBar slug={content.subsite.slug} passwordProtected={content.subsite.public !== false} />
            )}
            {/* Menu rendered outside wrapper so transform on wrapper doesn't affect fixed positioning */}
            {!isImageRoute && !isNotFound && !isBackgroundMode && (
                <Menu pages={pagesList} content={content} showMenu={showMenu} />
            )}
            <main 
                id="wrapper"
                className="flex flex-col flex-1"
                style={{ minHeight: '100dvh' }}
            >
                {!isImageRoute && !isNotFound && !isBackgroundMode && (
                    <Header
                        pages={pagesList}
                        properties={properties}
                        params={match?.params || {}}
                        file={null}
                        content={content}
                        theme={theme}
                    />
                )}
                <section 
                    id="page-wrapper" 
                    className="relative flex flex-col flex-1 min-h-0"
                >
                    {/* Underlay: old page content kept visible while ImageViewer fades in */}
                    {imageUnderlayContent && (
                        <article className="absolute inset-0 z-0 overflow-auto">
                            {imageUnderlayContent}
                        </article>
                    )}
                    
                    <article
                        ref={pageArticleRef}
                        className={classNames('flex-1 min-h-0 flex', revealPending ? 'opacity-0 pointer-events-none' : 'opacity-100')}
                        style={ENABLE_VIEWER_PAGE_FADES && !revealPending
                            ? { transition: `opacity ${IMAGE_FADE_DURATION}ms ease-in-out` }
                            : undefined}
                        data-page={slug}
                    >
                        {pageContent}
                    </article>
                </section>
            </main>
        </div>
    );
}

RootLayout.propTypes = {
    match: PropTypes.object,
    children: PropTypes.node,
    location: PropTypes.object,
};

export default RootLayout;
